import React, { useState, useEffect, useMemo } from 'react';
import {
  Container, Box, Paper, Typography, Skeleton, Alert,
  FormGroup, FormControlLabel, Checkbox
} from '@mui/material';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { SEASONS } from '../seasons';
import { logEvent } from '../analytics';
import { fetchAndProcessData } from '../utils/hockeyStatsParser';

const CrossSeasonTrends = () => {
    const [chartData, setChartData] = useState([]);
    const [allTeams, setAllTeams] = useState([]);
    const [visibleTeams, setVisibleTeams] = useState({});
    const [visibleSeasons, setVisibleSeasons] = useState({});
    const [hoveredTeam, setHoveredTeam] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const fetchAllStandings = async () => {
            document.title = "Jets '14 | Trends";
            try {
                const seasonPromises = SEASONS.map(async (seasonConfig) => {
                    const { seasonId, divisionId } = seasonConfig;
                    const { standings } = await fetchAndProcessData(seasonId, divisionId);
                    return { seasonLabel: seasonConfig.label, standings };
                });

                const allSeasonData = await Promise.all(seasonPromises);
                
                const uniqueTeams = [...new Set(allSeasonData.flatMap(season => season.standings.map(team => team.name)))].sort();
                setAllTeams(uniqueTeams);

                const initialTeamVisibility = uniqueTeams.reduce((acc, teamName) => {
                    acc[teamName] = true;
                    return acc;
                }, {});
                setVisibleTeams(initialTeamVisibility);
                
                const initialSeasonVisibility = SEASONS.reduce((acc, season) => {
                    acc[season.key] = season.key !== 'pw_25_26'; // Hide 25/26 by default
                    return acc;
                }, {});
                setVisibleSeasons(initialSeasonVisibility);

                const data = SEASONS.map(season => {
                    const seasonResult = allSeasonData.find(s => s.seasonLabel === season.label);
                    const seasonRanks = { season: season.label.substring(0, 5), key: season.key };
                    uniqueTeams.forEach(teamName => {
                        const teamStanding = seasonResult?.standings.find(t => t.name === teamName);
                        seasonRanks[teamName] = teamStanding ? teamStanding.rank : null;
                    });
                    return seasonRanks;
                });

                setChartData(data);

            } catch (e) {
                setError(e.message);
                console.error("Failed to fetch cross-season standings:", e);
            } finally {
                setLoading(false);
            }
        };

        fetchAllStandings();
    }, []);

    const teamStyles = useMemo(() => {
        const colorPalette = ['#8884d8', '#82ca9d', '#ffc658', '#ff7300', '#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#A28D82', '#D8A282', '#82A2D8', '#82D8A2', '#D882A2', '#A1D882', '#D882D8'];
        const dashPatterns = ['5 5', '10 5', '5 10'];
        return allTeams.reduce((acc, teamName, index) => {
            const colorIndex = index % colorPalette.length;
            const patternCycle = Math.floor(index / colorPalette.length);
            acc[teamName] = {
                color: colorPalette[colorIndex],
                strokeDasharray: patternCycle > 0 ? dashPatterns[(patternCycle - 1) % dashPatterns.length] : undefined,
            };
            return acc;
        }, {});
    }, [allTeams]);

    const handleTeamFilterChange = (event) => {
        setVisibleTeams({
            ...visibleTeams,
            [event.target.name]: event.target.checked,
        });
        logEvent('filter_team_trends', { team_name: event.target.name, visible: event.target.checked });
    };
    
    const handleSeasonFilterChange = (event) => {
        setVisibleSeasons({
            ...visibleSeasons,
            [event.target.name]: event.target.checked,
        });
        logEvent('filter_season_trends', { season_key: event.target.name, visible: event.target.checked });
    };

    const filteredChartData = chartData.filter(dataPoint => visibleSeasons[dataPoint.key]);

    const CustomTooltip = ({ active, payload, label }) => {
        if (active && payload && payload.length && hoveredTeam) {
            const hoveredPayload = payload.find(p => p.dataKey === hoveredTeam);
            if (!hoveredPayload) return null;

            return (
                <Paper elevation={3} sx={{ p: 2 }}>
                    <Typography variant="subtitle2">{label}</Typography>
                    <Typography variant="body2" sx={{ color: hoveredPayload.color }}>
                        {`${hoveredPayload.name}: Rank ${hoveredPayload.value}`}
                    </Typography>
                </Paper>
            );
        }
        return null;
    };

    return (
        <Container maxWidth="lg">
            <Box sx={{ my: 4 }}>
                <Paper variant="outlined" elevation={0} sx={{ p: { xs: 2, sm: 4 }, borderRadius: 2 }}>
                    <Typography variant="h4" component="h1" gutterBottom textAlign="center">
                        Cross-Season Standings Trends
                    </Typography>
                    
                    {error && <Alert severity="error">Could not load trend data: {error}</Alert>}
                    
                    {loading ? (
                        <Skeleton variant="rectangular" width="100%" height={600} />
                    ) : (
                        <>
                            <ResponsiveContainer width="100%" height={600}>
                                <LineChart
                                    data={filteredChartData}
                                    margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                                >
                                    <CartesianGrid strokeDasharray="3 3" />
                                    <XAxis dataKey="season" />
                                    <YAxis reversed={true} domain={['dataMin', 'dataMax']} label={{ value: 'Rank', angle: -90, position: 'insideLeft' }} />
                                    <Tooltip content={<CustomTooltip />} cursor={{ strokeDasharray: '3 3' }} />
                                    <Legend onMouseEnter={(e) => setHoveredTeam(e.dataKey)} onMouseLeave={() => setHoveredTeam(null)} />
                                    {allTeams.map(teamName => (
                                        visibleTeams[teamName] && <Line 
                                            key={teamName} 
                                            type="linear" 
                                            dataKey={teamName} 
                                            stroke={teamStyles[teamName]?.color} 
                                            strokeWidth={hoveredTeam === teamName ? 4 : 2}
                                            strokeOpacity={hoveredTeam && hoveredTeam !== teamName ? 0.3 : 1}
                                            connectNulls 
                                            strokeDasharray={teamStyles[teamName]?.strokeDasharray}
                                            onMouseEnter={() => setHoveredTeam(teamName)}
                                            onMouseLeave={() => setHoveredTeam(null)}
                                        />
                                    ))}
                                </LineChart>
                            </ResponsiveContainer>
                            <Box sx={{ mt: 4 }}>
                                <Typography variant="h6">Filter Seasons</Typography>
                                <FormGroup sx={{ display: 'flex', flexDirection: 'row', flexWrap: 'wrap' }}>
                                    {SEASONS.map(season => (
                                        <FormControlLabel
                                            key={season.key}
                                            control={
                                                <Checkbox
                                                    checked={visibleSeasons[season.key] || false}
                                                    onChange={handleSeasonFilterChange}
                                                    name={season.key}
                                                />
                                            }
                                            label={season.label}
                                        />
                                    ))}
                                </FormGroup>
                            </Box>
                            <Box sx={{ mt: 2 }}>
                                <Typography variant="h6">Filter Teams</Typography>
                                <FormGroup sx={{ display: 'flex', flexDirection: 'row', flexWrap: 'wrap' }}>
                                    {allTeams.map(teamName => (
                                        <FormControlLabel
                                            key={teamName}
                                            control={
                                                <Checkbox
                                                    checked={visibleTeams[teamName] || false}
                                                    onChange={handleTeamFilterChange}
                                                    name={teamName}
                                                    sx={{color: teamStyles[teamName]?.color, '&.Mui-checked': {color: teamStyles[teamName]?.color}}}
                                                />
                                            }
                                            label={teamName}
                                        />
                                    ))}
                                </FormGroup>
                            </Box>
                        </>
                    )}
                </Paper>
            </Box>
        </Container>
    );
};

export default CrossSeasonTrends;